import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import Table, { Column } from '../components/Table';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';

interface LeaveType {
  id: string;
  code: string;
  name: string;
  accrual_method: string;
  accrual_quantity: number;
  is_paid: boolean;
  is_active: boolean;
  is_leave: boolean;
  pay_type: string;
  leave_group_id: string;
  hide_from_application: boolean;
}
interface LeaveGroup {
  id: string;
  code: string;
  name: string;
}

const EMPTY_FORM = {
  code: '',
  name: '',
  description: '',
  accrualMethod: 'ANNUAL',
  accrualQuantity: 20,
  carryForwardAllowed: false,
  carryForwardLimit: '',
  isPaid: true,
  requiresApproval: true,
  isLeave: true,
  payType: 'PAID',
  leaveGroupId: '',
  hideFromApplication: false,
};

const PAY_TYPE_OPTIONS = [
  { value: 'PAID', label: 'Paid' },
  { value: 'FIRST_HALF_PAID', label: 'First Half Paid' },
  { value: 'SECOND_HALF_PAID', label: 'Second Half Paid' },
  { value: 'HOURLY_PAID', label: 'Hourly Paid' },
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'ABSENT', label: 'Absent' },
];

export default function LeaveTypes() {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [leaveGroups, setLeaveGroups] = useState<LeaveGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<LeaveType | null>(null);

  useEffect(() => {
    fetchLeaveTypes();
    api.get('/lms/masters/leave-groups').then((res) => setLeaveGroups(res.data.leaveGroups || []));
  }, []);

  const fetchLeaveTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/lms/leave-types');
      setLeaveTypes(response.data.leaveTypes || []);
    } catch (error) {
      console.error('Failed to fetch leave types:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (type: LeaveType) => {
    setEditingId(type.id);
    setFormData({
      ...EMPTY_FORM,
      code: type.code,
      name: type.name,
      accrualMethod: type.accrual_method,
      accrualQuantity: type.accrual_quantity,
      isPaid: type.is_paid,
      isLeave: type.is_leave !== false,
      payType: type.pay_type || 'PAID',
      leaveGroupId: type.leave_group_id || '',
      hideFromApplication: type.hide_from_application || false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/lms/leave-types/${editingId}`, formData);
      } else {
        await api.post('/lms/leave-types', formData);
      }
      setFormData(EMPTY_FORM);
      setEditingId(null);
      setShowForm(false);
      fetchLeaveTypes();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save leave type');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/lms/leave-types/${deleteTarget.id}`);
      setDeleteTarget(null);
      fetchLeaveTypes();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to deactivate leave type');
    }
  };

  const columns: Column<LeaveType>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'accrual_method', header: 'Accrual Method' },
    { key: 'accrual_quantity', header: 'Quantity', render: (row) => `${row.accrual_quantity} days` },
    {
      key: 'is_paid',
      header: 'Paid',
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-semibold ${row.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.is_paid ? 'Paid' : 'Unpaid'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="flex gap-2">
          <button onClick={() => openEditForm(row)} className="text-blue-600 hover:underline text-sm">
            Edit
          </button>
          <button onClick={() => setDeleteTarget(row)} className="text-red-600 hover:underline text-sm">
            Deactivate
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Leave Types</h1>
        <Button onClick={openCreateForm}>Create Leave Type</Button>
      </div>

      <Table columns={columns} rows={leaveTypes} loading={loading} emptyMessage="No leave types yet" />

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editingId ? 'Edit Leave Type' : 'Create Leave Type'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              disabled={!!editingId}
            />
            <Input
              label="Leave Type Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <Select
              label="Accrual Method"
              value={formData.accrualMethod}
              onChange={(e) => setFormData({ ...formData, accrualMethod: e.target.value })}
              options={[
                { value: 'ANNUAL', label: 'Annual' },
                { value: 'MONTHLY', label: 'Monthly' },
                { value: 'FIXED', label: 'Fixed' },
                { value: 'ON_HIRE', label: 'On Hire' },
              ]}
            />
            <Input
              label="Accrual Quantity (days)"
              type="number"
              value={formData.accrualQuantity}
              onChange={(e) => setFormData({ ...formData, accrualQuantity: parseFloat(e.target.value) })}
            />
            <Input
              label="Carry Forward Limit (days)"
              type="number"
              placeholder="optional"
              value={formData.carryForwardLimit}
              onChange={(e) => setFormData({ ...formData, carryForwardLimit: e.target.value })}
            />
            <Select
              label="Pay Type"
              value={formData.payType}
              onChange={(e) => setFormData({ ...formData, payType: e.target.value })}
              options={PAY_TYPE_OPTIONS}
            />
            <Select
              label="Leave Group (balance deducts from)"
              value={formData.leaveGroupId}
              onChange={(e) => setFormData({ ...formData, leaveGroupId: e.target.value })}
              options={leaveGroups.map((g) => ({ value: g.id, label: g.name }))}
            />
            <textarea
              placeholder="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="px-4 py-2 border rounded-lg col-span-2"
              rows={2}
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.carryForwardAllowed}
                onChange={(e) => setFormData({ ...formData, carryForwardAllowed: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Allow Carry Forward</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isPaid}
                onChange={(e) => setFormData({ ...formData, isPaid: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Is Paid Leave</span>
            </label>
            <label className="flex items-center gap-2 col-span-2">
              <input
                type="checkbox"
                checked={formData.requiresApproval}
                onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Requires Approval</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isLeave}
                onChange={(e) => setFormData({ ...formData, isLeave: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Is Leave (uncheck for TMS reason code only)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.hideFromApplication}
                onChange={(e) => setFormData({ ...formData, hideFromApplication: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Hide from Application</span>
            </label>
          </div>
          <Button type="submit" className="w-full">
            {editingId ? 'Save Changes' : 'Create Leave Type'}
          </Button>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Deactivate Leave Type"
        message={`Deactivate "${deleteTarget?.name}"? Existing balances/applications are unaffected.`}
        confirmLabel="Deactivate"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
